import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

const TICKER = [
  "LAND · KOKAPET",
  "HAND LOAN · 18%",
  "CORPORATE CONNECTIONS",
  "BEAN BOARD · KOKAPET",
  "LAND · KOKAPET",
  "HAND LOAN · 18%",
  "CORPORATE CONNECTIONS",
  "BEAN BOARD · KOKAPET",
];

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email, password);
    setBusy(false);
    if (r.ok) { toast.success("Welcome back"); nav("/"); }
    else { toast.error(r.error); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.15fr,1fr] bg-[#FAFAF7]">
      {/* Brand panel — editorial */}
      <div className="hidden lg:flex bg-[#DC2626] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-100" />
        {/* Corner registration marks */}
        <div className="absolute top-6 left-6 flex items-center gap-2 text-[10px] tracking-[0.35em] opacity-80">
          <span className="h-px w-6 bg-white/70" /> LLP · HYD · INDIA
        </div>
        <div className="absolute top-6 right-6 text-[10px] tracking-[0.35em] opacity-80">EST. 2024</div>
        <div className="absolute bottom-6 left-6 text-[10px] tracking-[0.35em] opacity-80">SECURE · ENCRYPTED · LOCAL</div>
        <div className="absolute bottom-6 right-6 flex items-center gap-2 text-[10px] tracking-[0.35em] opacity-80">
          v1.0 <span className="h-px w-6 bg-white/70" />
        </div>

        <div className="relative flex flex-col justify-between w-full px-12 xl:px-16 py-16">
          <div className="flex items-center gap-4">
            <img src="/marksam-logo-white.png" alt="MARKSAM Ventures" className="h-14 w-auto" />
          </div>

          <div className="max-w-xl">
            <div className="text-[11px] tracking-[0.35em] uppercase mb-8 opacity-80 flex items-center gap-3">
              <span className="h-px w-8 bg-white" /> The Ledger
            </div>
            <h1 className="font-editorial font-light text-[64px] xl:text-[86px] leading-[0.92] tracking-[-0.045em]">
              Every rupee<br />
              <em className="font-editorial italic font-light">accounted for.</em>
            </h1>
            <p className="mt-8 text-white/85 leading-relaxed text-lg max-w-md">
              A quiet source of truth for MARKSAM partners.
              Investments, income, loans, payouts — plainly.
            </p>
          </div>

          {/* Ticker */}
          <div className="w-full overflow-hidden border-y border-white/20 py-3 relative">
            <div className="flex gap-10 whitespace-nowrap ticker-track">
              {[...TICKER, ...TICKER].map((t, i) => (
                <span key={i} className="text-[11px] tracking-[0.3em] opacity-80 font-mono-fin">
                  {t} <span className="mx-6 opacity-40">◆</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center px-6 py-16 relative">
        {/* Mobile logo header */}
        <div className="lg:hidden absolute top-6 left-6 right-6 flex items-center justify-between">
          <div className="bg-red-600 p-1.5">
            <img src="/marksam-logo-white.png" alt="MARKSAM" className="h-7 w-auto" />
          </div>
          <div className="text-[10px] tracking-[0.3em] text-slate-500">v1.0</div>
        </div>

        <div className="w-full max-w-sm">
          <div className="flex items-baseline justify-between mb-1">
            <div className="eyebrow">Partners &amp; Team</div>
            <div className="eyebrow">01 / 01</div>
          </div>
          <h2 className="font-editorial font-light text-5xl text-slate-900 tracking-[-0.03em] leading-none mt-1">
            Sign <em className="italic font-light">in.</em>
          </h2>
          <p className="mt-4 text-sm text-slate-500 leading-relaxed max-w-[22rem]">
            Access the tracker with the email and password issued to you by the Managing Partner.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-6" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="eyebrow">Email address</Label>
              <Input
                id="email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} required
                data-testid="login-email"
                className="mt-2 rounded-none border-0 border-b border-slate-300 focus-visible:ring-0 focus-visible:border-red-600 bg-transparent h-11 px-0 text-base"
              />
            </div>
            <div>
              <Label htmlFor="password" className="eyebrow">Password</Label>
              <Input
                id="password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required
                data-testid="login-password"
                className="mt-2 rounded-none border-0 border-b border-slate-300 focus-visible:ring-0 focus-visible:border-red-600 bg-transparent h-11 px-0 text-base"
              />
            </div>
            <Button
              type="submit" disabled={busy}
              data-testid="login-submit"
              className="w-full rounded-none bg-slate-900 hover:bg-red-600 text-white h-12 tracking-[0.1em] font-medium text-sm uppercase transition-colors"
            >
              {busy ? "Signing in…" : "Sign in →"}
            </Button>
          </form>

          <div className="mt-16 flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-slate-400">
            <span>© MARKSAM Ventures LLP</span>
            <span>FY Apr–Mar · INR</span>
          </div>
        </div>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
